import { Zip, ZipDeflate, strToU8 } from 'fflate';
import { getCalibriGdiWidth } from './calibriMetrics';

/**
 * Builds an .xlsx in the browser that reproduces what the API's ExportHelper (EPPlus 7) writes
 * through AddWorksheet: same package parts in the same order, same styles (Calibri 11, header
 * row filled #D6DCE4), text cells as shared strings, the same integer/decimal cell conversion,
 * and column widths from the same auto-fit logic EPPlus runs on the server. Used for exports
 * that are too large to build on the API within Azure's 230-second request limit.
 */

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const BOM = '﻿';
const ROWS_PER_CHUNK = 2000;

const ROOT_RELS_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
const RELATIONSHIP_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml';

// ---- Cell styles (cellXfs), in the order EPPlus first creates them ----
const CELL_XF = {
    default: '<xf numFmtId="0" fontId="0" xfId="0" />',
    // Header fill only (sheets that skip AddWorksheet's number formatting, e.g. RawExamData).
    header: '<xf numFmtId="0" fontId="0" fillId="2" applyFill="1" />',
    // Header fill + centred text (AddWorksheet's number-formatting branch).
    headerCentered: '<xf numFmtId="0" fontId="0" fillId="2" applyFill="1" applyAlignment="1"><alignment horizontal="center" /></xf>',
    integer: '<xf numFmtId="1" applyNumberFormat="1" fontId="0" applyAlignment="1"><alignment horizontal="right" /></xf>',
    decimal: '<xf numFmtId="2" applyNumberFormat="1" fontId="0" applyAlignment="1"><alignment horizontal="right" /></xf>',
};

const buildStylesXml = (styleKeys) =>
    `${BOM}<?xml version="1.0" encoding="utf-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="0" /><fonts count="1"><font><sz val="11" /><name val="Calibri" /></font></fonts><fills count="3"><fill><patternFill patternType="none" /></fill><fill><patternFill patternType="gray125" /></fill><fill><patternFill patternType="solid"><fgColor rgb="FFD6DCE4" /></patternFill></fill></fills><borders count="1"><border><left /><right /><top /><bottom /><diagonal /></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" /></cellStyleXfs><cellXfs count="${styleKeys.length}">${styleKeys.map((key) => CELL_XF[key]).join('')}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0" /></cellStyles><dxfs count="0" /></styleSheet>`;

// ---- Column auto-fit (EPPlus AutofitHelper with the System.Drawing text measurer) ----
// GDI+ MeasureString(text, Calibri 11pt, GenericDefault) in pixels is linear in the summed
// glyph advances: advanceUnits * PX_PER_UNIT + PADDING_PX (1/6 em margin on each side).
// Constants are a least-squares fit against GDI+ over a full Raw Exam Data export.
const GDI_PX_PER_UNIT = 0.007161456679238159;
const GDI_PADDING_PX = 4.888893618354419;
const GDI_LAYOUT_WIDTH_PX = 10000;
const GDI_TRAILING_WHITESPACE = /[   - 　]+$/;
const NORMAL_FONT_WIDTH_PX = 7; // FontSize.GetWidthPixels("Calibri", 11)
const DEFAULT_COLUMN_WIDTH = 9.140625; // ExcelWorksheet.DefaultColWidth, the auto-fit minimum
const MAX_MEASURED_LENGTH = 32000;

const measureColumnWidth = (text) => {
    const measured = (text.length > MAX_MEASURED_LENGTH ? text.substring(0, MAX_MEASURED_LENGTH) : text)
        .replace(GDI_TRAILING_WHITESPACE, '');
    if (!measured) return 0;

    let units = 0;
    for (const ch of measured) {
        units += getCalibriGdiWidth(ch.codePointAt(0));
    }
    // GDI+ returns a float; EPPlus' division by the normal font width happens at double precision.
    const px = Math.min(Math.fround(units * GDI_PX_PER_UNIT + GDI_PADDING_PX), GDI_LAYOUT_WIDTH_PX);
    return px / NORMAL_FONT_WIDTH_PX;
};

// .NET Framework double.ToString() (15 significant digits), as EPPlus writes column widths.
const formatDotNetDouble = (value) => String(Number(value.toPrecision(15)));

// ---- AddWorksheet number conversion ----
// Int16/Int32/Int64/Double columns: int.TryParse(text) -> number, format "0", right aligned.
// Decimal columns: decimal.TryParse(text) -> number, format "0.00", right aligned.
export const COLUMN_TYPE = { TEXT: 'text', INTEGER: 'integer', DECIMAL: 'decimal' };

const INT32_PATTERN = /^\s*[+-]?\d+\s*$/;
const DECIMAL_PATTERN = /^\s*[+-]?(\d+(\.\d*)?|\.\d+)\s*$/;

// Returns { value, display, style } for a cell AddWorksheet would convert to a number, else null.
const toNumericCell = (text, columnType) => {
    if (columnType === COLUMN_TYPE.INTEGER) {
        if (!INT32_PATTERN.test(text)) return null;
        const number = Number(text);
        if (number < -2147483648 || number > 2147483647) return null;
        return { value: String(number), display: String(number), style: 'integer' };
    }
    if (columnType === COLUMN_TYPE.DECIMAL) {
        if (!DECIMAL_PATTERN.test(text)) return null;
        const number = Number(text);
        return { value: String(number), display: number.toFixed(2), style: 'decimal' };
    }
    return null;
};

// ---- Shared string encoding (EPPlus SaveSharedStringHandler + ConvertUtil) ----
const needsPreserveSpace = (t) =>
    t.length > 0 &&
    (t[0] === ' ' || t[t.length - 1] === ' ' || t.includes('  ') || t.includes('\r') || t.includes('\t') || t.includes('\n'));

const encodeControlChar = (ch) => {
    const code = ch.charCodeAt(0);
    const hex = code.toString(16).toUpperCase();
    return code > 0xFFFD ? `_x${hex}_` : `_x00${code <= 0xF ? '0' : ''}${hex}_`;
};

// EPPlus writes through a UTF-8 encoder whose replacement fallback is empty, so unpaired
// surrogates are dropped from the file.
const removeUnpairedSurrogates = (t) => {
    if (!/[\uD800-\uDFFF]/.test(t)) return t;
    let result = '';
    for (let i = 0; i < t.length; i++) {
        const code = t.charCodeAt(i);
        if (code >= 0xD800 && code <= 0xDBFF) {
            const next = t.charCodeAt(i + 1);
            if (next >= 0xDC00 && next <= 0xDFFF) {
                result += t[i] + t[i + 1];
                i++;
            }
        } else if (code < 0xDC00 || code > 0xDFFF) {
            result += t[i];
        }
    }
    return result;
};

const encodeSharedString = (t) => {
    const escaped = t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/_x[0-9A-F]{4}_/g, (match) => `_x005F${match}`)
        .replace(/[\u0000-\u0009\u000B-\u001F￾￿]/g, encodeControlChar); // eslint-disable-line no-control-regex
    const open = needsPreserveSpace(t) ? '<si><t xml:space="preserve">' : '<si><t>';
    return `${open}${removeUnpairedSurrogates(escaped)}</t></si>`;
};

const escapeXmlAttribute = (value) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const getColumnLetter = (index) => {
    let letter = '';
    for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
        letter = String.fromCharCode(65 + ((n - 1) % 26)) + letter;
    }
    return letter;
};

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

const createZipWriter = () => {
    const chunks = [];
    let resolveBlob;
    let rejectBlob;
    const blob = new Promise((resolve, reject) => {
        resolveBlob = resolve;
        rejectBlob = reject;
    });
    const zip = new Zip((err, data, final) => {
        if (err) {
            rejectBlob(err);
            return;
        }
        chunks.push(data);
        if (final) resolveBlob(new Blob(chunks, { type: XLSX_MIME_TYPE }));
    });

    const addFile = (name, text) => {
        const file = new ZipDeflate(name, { level: 6 });
        zip.add(file);
        file.push(strToU8(text), true);
    };

    const addChunkedFile = async (name, pieces) => {
        const file = new ZipDeflate(name, { level: 6 });
        zip.add(file);
        for (const piece of pieces) {
            file.push(strToU8(piece), false);
            await yieldToBrowser();
        }
        file.push(new Uint8Array(0), true);
    };

    const finish = () => {
        zip.end();
        return blob;
    };

    return { addFile, addChunkedFile, finish };
};

/**
 * @typedef {Object} EpplusSheet
 * @property {string} name
 * @property {string[]} headers
 * @property {string[][]} rows Cell text exactly as the API's 2D export array held it.
 * @property {string[]} [columnTypes] COLUMN_TYPE per column (the DataTable column type).
 * @property {boolean} [formatNumbers] true for sheets that go through AddWorksheet's number
 *   formatting branch (every sheet except "RawExamData").
 */

/**
 * @param {EpplusSheet[]} sheets
 * @returns {Promise<Blob>}
 */
export const buildEpplusWorkbook = async (sheets) => {
    const styleKeys = ['default'];
    const styleId = (key) => {
        let index = styleKeys.indexOf(key);
        if (index < 0) {
            styleKeys.push(key);
            index = styleKeys.length - 1;
        }
        return index;
    };
    const sharedStringIndex = new Map();
    const widthCache = new Map();
    const addSharedString = (text) => {
        if (!sharedStringIndex.has(text)) sharedStringIndex.set(text, sharedStringIndex.size);
    };
    const measure = (text) => {
        let width = widthCache.get(text);
        if (width === undefined) {
            width = measureColumnWidth(text);
            widthCache.set(text, width);
        }
        return width;
    };

    // Pass 1, per sheet in order: shared strings (first appearance, row by row), auto-fit
    // widths, and the cell styles in the order AddWorksheet creates them.
    const prepared = sheets.map((sheet) => {
        const { headers, rows } = sheet;
        const columnCount = headers.length;
        const columnTypes = sheet.columnTypes || headers.map(() => COLUMN_TYPE.TEXT);
        const formatNumbers = Boolean(sheet.formatNumbers);
        const columnWidths = new Array(columnCount).fill(DEFAULT_COLUMN_WIDTH);
        const numericStyleUsed = new Array(columnCount).fill(null);

        if (!formatNumbers) styleId('header');

        headers.forEach((text, col) => {
            addSharedString(text ?? '');
            columnWidths[col] = Math.max(columnWidths[col], measure(text ?? ''));
        });
        rows.forEach((row) => {
            for (let col = 0; col < columnCount; col++) {
                const text = row[col] ?? '';
                const numeric = formatNumbers ? toNumericCell(text, columnTypes[col]) : null;
                if (numeric) {
                    numericStyleUsed[col] = numeric.style;
                    columnWidths[col] = Math.max(columnWidths[col], measure(numeric.display));
                } else {
                    addSharedString(text);
                    columnWidths[col] = Math.max(columnWidths[col], measure(text));
                }
            }
        });

        // AddWorksheet walks the columns converting numbers, then centres that column's header.
        if (formatNumbers) {
            for (let col = 0; col < columnCount; col++) {
                if (numericStyleUsed[col]) styleId(numericStyleUsed[col]);
                styleId('headerCentered');
            }
        }

        return { ...sheet, columnCount, columnTypes, formatNumbers, columnWidths };
    });

    const sheetPieces = function* (sheet) {
        const { headers, rows, columnCount, columnTypes, formatNumbers, columnWidths } = sheet;
        const columnLetters = headers.map((_, index) => getColumnLetter(index));
        const headerStyle = styleId(formatNumbers ? 'headerCentered' : 'header');
        const cols = columnWidths
            .map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${formatDotNetDouble(width)}" customWidth="1"/>`)
            .join('');

        yield `${BOM}<?xml version="1.0" encoding="utf-8"?><worksheet xmlns:r="${RELATIONSHIP_TYPE}" xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetPr codeName="" /><dimension ref="A1:${columnLetters[columnCount - 1]}${rows.length + 1}" /><sheetViews><sheetView workbookViewId="0" /></sheetViews><sheetFormatPr defaultRowHeight="15" /><cols>${cols}</cols><sheetData>`;

        let xml = '<row r="1">';
        for (let col = 0; col < columnCount; col++) {
            xml += `<c r="${columnLetters[col]}1" s="${headerStyle}" t="s"><v>${sharedStringIndex.get(headers[col] ?? '')}</v></c>`;
        }
        xml += '</row>';

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r];
            const rowNumber = r + 2;
            xml += `<row r="${rowNumber}">`;
            for (let col = 0; col < columnCount; col++) {
                const text = row[col] ?? '';
                const numeric = formatNumbers ? toNumericCell(text, columnTypes[col]) : null;
                xml += numeric
                    ? `<c r="${columnLetters[col]}${rowNumber}" s="${styleId(numeric.style)}"><v>${numeric.value}</v></c>`
                    : `<c r="${columnLetters[col]}${rowNumber}" s="0" t="s"><v>${sharedStringIndex.get(text)}</v></c>`;
            }
            xml += '</row>';
            if ((r + 1) % ROWS_PER_CHUNK === 0) {
                yield xml;
                xml = '';
            }
        }
        yield `${xml}</sheetData><headerFooter /></worksheet>`;
    };

    function* sharedStringPieces() {
        yield `${BOM}<?xml version="1.0" encoding="UTF-8" standalone="yes" ?><sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${sharedStringIndex.size}" uniqueCount="${sharedStringIndex.size}">`;
        let xml = '';
        let count = 0;
        for (const text of sharedStringIndex.keys()) {
            xml += encodeSharedString(text);
            if (++count % 20000 === 0) {
                yield xml;
                xml = '';
            }
        }
        yield `${xml}</sst>`;
    }

    // Package layout as EPPlus saves it: sheet1 is created before styles, later sheets after,
    // and the shared strings last. Relationship ids follow the same creation order.
    const sheetRelId = (index) => (index === 0 ? 'rId1' : `rId${index + 2}`);
    const sharedStringsRelId = `rId${sheets.length + 2}`;

    const contentTypeOverrides = [
        `<Override ContentType="${CONTENT_TYPE}.sheet.main+xml" PartName="/xl/workbook.xml" />`,
        `<Override ContentType="${CONTENT_TYPE}.worksheet+xml" PartName="/xl/worksheets/sheet1.xml" />`,
        `<Override ContentType="${CONTENT_TYPE}.styles+xml" PartName="/xl/styles.xml" />`,
        ...prepared.slice(1).map((_, i) => `<Override ContentType="${CONTENT_TYPE}.worksheet+xml" PartName="/xl/worksheets/sheet${i + 2}.xml" />`),
        `<Override ContentType="${CONTENT_TYPE}.sharedStrings+xml" PartName="/xl/sharedStrings.xml" />`,
    ];
    const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default ContentType="application/xml" Extension="xml"/><Default ContentType="application/vnd.openxmlformats-package.relationships+xml" Extension="rels"/>${contentTypeOverrides.join('')}</Types>`;

    const workbookRels = [
        `<Relationship Id="rId1" Type="${RELATIONSHIP_TYPE}/worksheet" Target="worksheets/sheet1.xml"/>`,
        `<Relationship Id="rId2" Type="${RELATIONSHIP_TYPE}/styles" Target="styles.xml"/>`,
        ...prepared.slice(1).map((_, i) => `<Relationship Id="${sheetRelId(i + 1)}" Type="${RELATIONSHIP_TYPE}/worksheet" Target="worksheets/sheet${i + 2}.xml"/>`),
        `<Relationship Id="${sharedStringsRelId}" Type="${RELATIONSHIP_TYPE}/sharedStrings" Target="sharedStrings.xml"/>`,
    ];
    const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${workbookRels.join('')}</Relationships>`;

    const sheetEntries = prepared
        .map((sheet, index) => `<sheet name="${escapeXmlAttribute(sheet.name)}" sheetId="${index + 1}" r:id="${sheetRelId(index)}" />`)
        .join('');
    const workbookXml = `${BOM}<?xml version="1.0" encoding="utf-8"?><workbook xmlns:r="${RELATIONSHIP_TYPE}" xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><bookViews><workbookView /></bookViews><sheets>${sheetEntries}</sheets><calcPr fullCalcOnLoad="1" /></workbook>`;

    // Pass 2: write the package in EPPlus' part order.
    const zip = createZipWriter();
    zip.addFile('[Content_Types].xml', contentTypesXml);
    zip.addFile('_rels/.rels', ROOT_RELS_XML);
    await zip.addChunkedFile('xl/worksheets/sheet1.xml', sheetPieces(prepared[0]));
    zip.addFile('xl/styles.xml', buildStylesXml(styleKeys));
    for (let i = 1; i < prepared.length; i++) {
        await zip.addChunkedFile(`xl/worksheets/sheet${i + 1}.xml`, sheetPieces(prepared[i]));
    }
    zip.addFile('xl/workbook.xml', workbookXml);
    zip.addFile('xl/_rels/workbook.xml.rels', workbookRelsXml);
    await zip.addChunkedFile('xl/sharedStrings.xml', sharedStringPieces());
    return zip.finish();
};

/**
 * Single text-only sheet, as ExportHelper.ExportToCSV writes "RawExamData" (no number formatting).
 * @param {{ sheetName: string, headers: string[], rows: string[][] }} options
 * @returns {Promise<Blob>}
 */
export const buildEpplusXlsx = ({ sheetName, headers, rows }) =>
    buildEpplusWorkbook([{ name: sheetName, headers, rows, formatNumbers: false }]);
